import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession, setImpersonationState, stashDeveloperSession } from '../lib/session';
import { Avatar } from '../components/Avatar';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { useToast } from '../components/Toast';
import { fetchDrawers, uploadDrawerDoorCover, setDrawerCoverImage } from '../lib/queries';
import {
  addAllowedEmail,
  removeAllowedEmail,
  setAllowedEmailFounding,
  fetchRecentFeedback,
  markFeedbackRead,
  type FeedbackRow,
} from '../lib/admin-queries';
import {
  fetchPrices,
  createPrice,
  formatCents,
  type PriceRow,
} from '../lib/billing-admin-queries';
import {
  fetchMembers,
  setMemberFounding,
  setAdminPermission,
  setMemberRole,
  updateMemberProfile,
  generateSignInLink,
  sendMemberInvite,
  deleteMember,
  type MemberRow,
} from '../lib/members-admin-queries';
import {
  fetchAccent,
  saveAccent,
  applyAccent,
  parseColour,
} from '../lib/accent';
import { ACCENT_PRESETS } from '../lib/accent-presets';
import type { Drawer, Role } from '../lib/types';
import { AdminBanners } from '../components/AdminBanners';
import { AdminWelcome } from '../components/admin/AdminWelcome';
import { ContentManager } from '../components/admin/ContentManager';
import { SaleroomCharges } from '../components/admin/SaleroomCharges';
import { SaleroomLiveBids } from '../components/admin/SaleroomLiveBids';
import { NotificationTester } from '../components/admin/NotificationTester';
import {
  fetchSaleroomCharges,
  countOutstandingSaleroomCharges,
  type SaleroomChargeRow,
} from '../lib/saleroom-admin-queries';
import { BulkEmailModal } from '../components/admin/BulkEmail';
import { EmailLog } from '../components/admin/EmailLog';
import './Admin.css';

type AdminSection =
  | 'members' | 'content' | 'feedback' | 'drawers' | 'billing' | 'saleroom' | 'appearance' | 'banners' | 'welcome' | 'emaillog' | 'admins' | 'dev';

const CATEGORY_LABELS: Record<string, string> = {
  tech: 'Tech',
  feature: 'Feature Request',
  feedback: 'Feedback',
  idea: 'Idea',
  billing: 'Billing',
  other: 'Other',
};

function readSectionFromHash(): AdminSection | null {
  if (typeof window === 'undefined') return null;
  const h = window.location.hash.replace(/^#/, '');
  if (
    h === 'members' || h === 'content' || h === 'feedback' ||
    h === 'drawers' || h === 'billing' || h === 'saleroom' || h === 'appearance' ||
    h === 'banners' || h === 'welcome' || h === 'emaillog' || h === 'admins' || h === 'dev'
  ) return h;
  // Back-compat: existing bookmarks pointing at #testers land on Members.
  if (h === 'testers') return 'members';
  return null;
}

export default function Admin() {
  const { profile, loading } = useSession();
  const toast = useToast();
  const [activeSection, setActiveSection] = useState<AdminSection>(
    () => readSectionFromHash() ?? 'members',
  );

  // Two-way hash <-> state sync so deep-links + back-button work.
  useEffect(() => {
    if (window.location.hash.replace(/^#/, '') !== activeSection) {
      window.history.replaceState(null, '', `#${activeSection}`);
    }
  }, [activeSection]);
  useEffect(() => {
    function onHashChange() {
      const fromHash = readSectionFromHash();
      if (fromHash) setActiveSection(fromHash);
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const [drawers, setDrawers] = useState<Drawer[]>([]);
  // Drawer whose door-image upload/remove is in flight (one at a time).
  const [doorBusy, setDoorBusy] = useState<string | null>(null);

  async function handleDoorCover(d: Drawer, file: File | null) {
    if (!file || doorBusy) return;
    setDoorBusy(d.id);
    try {
      const url = await uploadDrawerDoorCover({ drawerId: d.id, file });
      await setDrawerCoverImage(d.id, url);
      setDrawers((prev) => prev.map((x) => (x.id === d.id ? { ...x, cover_image_url: url } : x)));
      toast.success(`${d.name} door image set.`);
    } catch (err: any) {
      console.error('[admin] door cover upload failed', err);
      toast.error(err?.message ?? 'Upload failed');
    } finally {
      setDoorBusy(null);
    }
  }

  async function handleDoorCoverRemove(d: Drawer) {
    if (doorBusy) return;
    setDoorBusy(d.id);
    try {
      await setDrawerCoverImage(d.id, null);
      setDrawers((prev) => prev.map((x) => (x.id === d.id ? { ...x, cover_image_url: null } : x)));
      toast.success(`${d.name} door image removed.`);
    } catch (err: any) {
      console.error('[admin] door cover remove failed', err);
      toast.error(err?.message ?? 'Remove failed');
    } finally {
      setDoorBusy(null);
    }
  }
  const [impersonateUsers, setImpersonateUsers] = useState<Array<{ id: string; display_name: string | null; handle: string | null; role: string }>>([]);
  const [feedback, setFeedback] = useState<FeedbackRow[]>([]);
  const [feedbackTab, setFeedbackTab] = useState<'new' | 'all' | 'read'>('new');
  const [feedbackCategory, setFeedbackCategory] = useState<string>('all');
  const [feedbackBusy, setFeedbackBusy] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Saleroom charges live up here (not inside the panel) so the nav-tab
  // indicator can count outstanding work without the tab being opened.
  const [saleroomRows, setSaleroomRows] = useState<SaleroomChargeRow[]>([]);
  const [saleroomLoading, setSaleroomLoading] = useState(false);
  const [saleroomLoaded, setSaleroomLoaded] = useState(false);
  const [saleroomError, setSaleroomError] = useState<string | null>(null);

  const refreshSaleroom = useCallback(async () => {
    setSaleroomLoading(true);
    setSaleroomError(null);
    try {
      setSaleroomRows(await fetchSaleroomCharges());
    } catch (err: any) {
      setSaleroomError(err?.message ?? 'Failed to load Saleroom charges.');
    } finally {
      setSaleroomLoading(false);
      setSaleroomLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!profile) return;
    if (profile.role !== 'admin' && profile.role !== 'developer') return;

    const ctrl = new AbortController();
    (async () => {
      try {
        const [d, fb] = await Promise.all([
          fetchDrawers(ctrl.signal),
          fetchRecentFeedback(50, ctrl.signal).catch((err) => {
            if (err?.name === 'AbortError') throw err;
            return [];
          }),
        ]);
        if (ctrl.signal.aborted) return;
        setDrawers(d);
        setFeedback(fb);

        const canImp =
          profile.role === 'developer' ||
          (profile.role === 'admin' && profile.permissions?.can_impersonate === true);
        if (canImp) {
          const { data: ms } = await supabase
            .from('profiles')
            .select('id, display_name, handle, role')
            .order('created_at', { ascending: true })
            .abortSignal(ctrl.signal);
          // Non-developer admins may only impersonate regular members — the
          // server enforces this too (impersonate.ts target-role guard), but
          // filter the list so we don't show targets that would 403.
          const visible = (ms ?? []).filter(
            (u: any) => profile.role === 'developer' || u.role === 'user',
          );
          if (!ctrl.signal.aborted) setImpersonateUsers(visible as any);
        }
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        setError(err?.message ?? 'Failed to load admin data.');
      } finally {
        if (!ctrl.signal.aborted) setLoadingData(false);
      }
    })();
    return () => ctrl.abort();
  }, [profile?.id, profile?.role]);

  // Seed the Saleroom rows + nav badge eagerly so the team sees outstanding
  // charges without opening the tab.
  useEffect(() => {
    if (!profile) return;
    if (profile.role !== 'admin' && profile.role !== 'developer') return;
    void refreshSaleroom();
  }, [profile?.id, profile?.role, refreshSaleroom]);

  if (loading) return null;
  if (!profile || (profile.role !== 'admin' && profile.role !== 'developer')) {
    return <Navigate to="/" replace />;
  }

  const isDeveloper = profile.role === 'developer';
  const canImpersonate =
    isDeveloper || (profile.role === 'admin' && profile.permissions?.can_impersonate === true);

  return (
    <div className="admin">
      <div className="container">
        <header className="admin__head">
          <p className="kicker">Admin</p>
          <h1>
            {profile.display_name ? `${profile.display_name}'s desk.` : 'Your desk.'}
          </h1>
          <p className="admin__sub">Publish, manage testers, read what the room says back.</p>
          <p className="admin__role-pill">Signed in as <strong>{profile.role}</strong></p>
        </header>

        {error && (
          <div className="admin__error">
            <p className="kicker">Problem</p>
            <p>{error}</p>
          </div>
        )}

        <nav className="admin__sections" role="tablist" aria-label="Admin sections">
          {([
            { id: 'members',  label: 'Members',  badge: null },
            { id: 'content',  label: 'Content',  badge: null },
            { id: 'feedback', label: 'Contact', badge: feedback.filter((f) => !f.is_read).length || null },
            { id: 'drawers',  label: 'Drawers',  badge: null },
            { id: 'billing',  label: 'Billing',  badge: null },
            { id: 'saleroom', label: 'Saleroom', badge: countOutstandingSaleroomCharges(saleroomRows) || null },
            { id: 'appearance', label: 'Appearance', badge: null },
            { id: 'banners', label: 'Banners', badge: null },
            { id: 'welcome', label: 'Welcome', badge: null },
            { id: 'emaillog', label: 'Email Log', badge: null },
            ...(isDeveloper
              ? [{ id: 'admins' as const, label: 'Admins', badge: null }]
              : []),
            ...(canImpersonate
              ? [{ id: 'dev' as const, label: 'Impersonate', badge: null }]
              : []),
          ] as Array<{ id: AdminSection; label: string; badge: number | null }>).map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={activeSection === s.id}
              className={`admin__section ${activeSection === s.id ? 'admin__section--active' : ''}`}
              onClick={() => setActiveSection(s.id)}
            >
              {s.label}
              {s.badge ? <span className="admin__section-badge">{s.badge}</span> : null}
            </button>
          ))}
        </nav>

        <section className={`admin__panel ${activeSection === 'banners' ? '' : 'admin__panel--hidden'}`}>
          <AdminBanners active={activeSection === 'banners'} createdBy={profile.id} />
        </section>

        <section className={`admin__panel ${activeSection === 'welcome' ? '' : 'admin__panel--hidden'}`}>
          <AdminWelcome active={activeSection === 'welcome'} me={{ id: profile.id }} />
        </section>

        <section className={`admin__panel ${activeSection === 'members' ? '' : 'admin__panel--hidden'}`}>
          <MembersPanel
            active={activeSection === 'members'}
            me={{ id: profile.id, role: profile.role }}
          />
        </section>

        <section className={`admin__panel ${activeSection === 'content' ? '' : 'admin__panel--hidden'}`}>
          <ContentManager active={activeSection === 'content'} />
        </section>

        <section className={`admin__panel ${activeSection === 'feedback' ? '' : 'admin__panel--hidden'}`}>
          <div className="admin__panel-head">
            <h2>Contact inbox</h2>
            <span className="admin__count">
              {feedback.filter((f) => !f.is_read).length} new / {feedback.length} total
            </span>
            <label className="admin__feedback-filter">
              <span className="visually-hidden">Filter by category</span>
              <select
                value={feedbackCategory}
                onChange={(e) => setFeedbackCategory(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="all">All categories</option>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="admin__tabs" role="tablist" aria-label="Contact filter">
            {(['new', 'all', 'read'] as const).map((tab) => {
              const count =
                tab === 'new'  ? feedback.filter((f) => !f.is_read).length :
                tab === 'read' ? feedback.filter((f) =>  f.is_read).length :
                                 feedback.length;
              return (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={feedbackTab === tab}
                  className={`admin__tab ${feedbackTab === tab ? 'admin__tab--active' : ''}`}
                  onClick={() => setFeedbackTab(tab)}
                >
                  {tab === 'new' ? 'New' : tab === 'all' ? 'All' : 'Read'}
                  <span className="admin__tab-count">{count}</span>
                </button>
              );
            })}
          </div>

          {loadingData ? (
            <ul className="admin__feedback" aria-busy="true" aria-label="Loading messages">
              {Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="admin__feedback-item admin__feedback-item--skel">
                  <Skeleton variant="text" width="30%" height={12} />
                  <div style={{ height: 6 }} />
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="75%" />
                </li>
              ))}
            </ul>
          ) : (() => {
            const filtered = feedback.filter((f) => {
              const matchesTab =
                feedbackTab === 'new'  ? !f.is_read :
                feedbackTab === 'read' ?  f.is_read :
                                          true;
              const matchesCategory =
                feedbackCategory === 'all' || f.category === feedbackCategory;
              return matchesTab && matchesCategory;
            });
            if (filtered.length === 0) {
              return (
                <EmptyState
                  tone="compact"
                  align="start"
                  eyebrow={feedbackTab === 'new' ? 'Inbox zero' : 'Quiet so far'}
                  headline={
                    feedbackTab === 'new' ? 'Nothing new to read.' :
                    feedbackTab === 'read' ? 'Nothing in the archive yet.' :
                                             'No messages yet.'
                  }
                  body={
                    feedbackTab === 'read'
                      ? 'Items you mark as read land here.'
                      : 'Testers hit the floating Contact pill on any page, it lands here.'
                  }
                />
              );
            }
            return (
              <ul className="admin__feedback">
                {filtered.map((f) => (
                  <li
                    key={f.id}
                    className={`admin__feedback-item ${f.is_read ? 'admin__feedback-item--read' : 'admin__feedback-item--unread'}`}
                  >
                    <div className="admin__feedback-head">
                      <Avatar
                        name={f.author?.display_name || 'Anonymous'}
                        src={f.author?.avatar_url}
                        size="sm"
                      />
                      <div className="admin__feedback-meta">
                        <span className="admin__feedback-name">
                          {f.author?.display_name || 'Anonymous'}
                          {f.author?.handle && <span className="admin__feedback-handle"> @{f.author.handle}</span>}
                        </span>
                        <span className="admin__feedback-time">
                          {new Date(f.created_at).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}
                          {f.page_path && <> · <code>{f.page_path}</code></>}
                        </span>
                      </div>
                      {f.category && (
                        <span className="admin__feedback-category">
                          {CATEGORY_LABELS[f.category] ?? f.category}
                        </span>
                      )}
                      {!f.is_read && <span className="admin__feedback-dot" aria-label="Unread" />}
                    </div>
                    <p className="admin__feedback-message">{f.message}</p>
                    <div className="admin__feedback-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={feedbackBusy === f.id}
                        onClick={async () => {
                          setFeedbackBusy(f.id);
                          try {
                            await markFeedbackRead(f.id, !f.is_read);
                            setFeedback((prev) =>
                              prev.map((x) => (x.id === f.id ? { ...x, is_read: !f.is_read } : x)),
                            );
                            toast.success(f.is_read ? 'Marked unread.' : 'Marked read.');
                          } catch (err: any) {
                            toast.error(err?.message ?? 'Update failed.');
                          } finally {
                            setFeedbackBusy(null);
                          }
                        }}
                      >
                        {feedbackBusy === f.id
                          ? '…'
                          : f.is_read
                            ? 'Mark unread'
                            : 'Mark read'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            );
          })()}
        </section>

        <section className={`admin__panel ${activeSection === 'drawers' ? '' : 'admin__panel--hidden'}`}>
          <div className="admin__panel-head">
            <h2>Drawers</h2>
            <span className="admin__count">{drawers.length}</span>
          </div>
          <ul className="admin__drawers">
            {drawers.map((d) => (
              <li key={d.id} className="admin__drawer">
                <strong>{d.name}</strong>
                <code>{d.slug}</code>
                <div className="admin__drawer-door">
                  {d.cover_image_url ? (
                    <img src={d.cover_image_url} alt={`${d.name} door image`} />
                  ) : (
                    <span className="admin__drawer-door-empty">No door image</span>
                  )}
                  <div className="admin__drawer-door-actions">
                    <label className={`btn btn--secondary btn--sm ${doorBusy === d.id ? 'btn--disabled' : ''}`}>
                      {doorBusy === d.id ? '…' : d.cover_image_url ? 'Replace' : 'Set door image'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        disabled={doorBusy === d.id}
                        onChange={(e) => {
                          void handleDoorCover(d, e.target.files?.[0] ?? null);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {d.cover_image_url && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => void handleDoorCoverRemove(d)}
                        disabled={doorBusy === d.id}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <p className="admin__note">
            A door image fronts that closet's doors on the feed instead of the
            latest post's media. Members see it through the open doors.
          </p>
        </section>

        <section className={`admin__panel ${activeSection === 'billing' ? '' : 'admin__panel--hidden'}`}>
          <BillingPanel active={activeSection === 'billing'} />
        </section>

        <section className={`admin__panel ${activeSection === 'saleroom' ? '' : 'admin__panel--hidden'}`}>
          <SaleroomLiveBids active={activeSection === 'saleroom'} />
          <SaleroomCharges
            rows={saleroomRows}
            loading={saleroomLoading}
            loaded={saleroomLoaded}
            error={saleroomError}
            onRefresh={refreshSaleroom}
          />
          <NotificationTester />
        </section>

        <section className={`admin__panel ${activeSection === 'emaillog' ? '' : 'admin__panel--hidden'}`}>
          <EmailLog active={activeSection === 'emaillog'} />
        </section>

        <section className={`admin__panel ${activeSection === 'appearance' ? '' : 'admin__panel--hidden'}`}>
          <AppearancePanel active={activeSection === 'appearance'} />
        </section>

        {isDeveloper && (
          <section className={`admin__panel ${activeSection === 'admins' ? '' : 'admin__panel--hidden'}`}>
            <AdminsPanel active={activeSection === 'admins'} meId={profile.id} />
          </section>
        )}

        {canImpersonate && (
          <section className={`admin__panel ${activeSection === 'dev' ? '' : 'admin__panel--hidden'}`}>
            <div className="admin__panel-head">
              <h2>Impersonate a member</h2>
              <span className="admin__count">{impersonateUsers.length} members</span>
            </div>
            <p className="admin__note">
              Sign in as any member to see their exact view. All actions during an impersonation
              session are written to <code>impersonation_log</code>.
            </p>
            <ul className="admin__posts">
              {impersonateUsers.map((m) => (
                <li key={m.id} className="admin__post">
                  <div className="admin__post-meta">
                    <h3>{m.display_name || m.handle || m.id}</h3>
                    <p className="admin__post-stats">{m.role} · {m.handle ? `@${m.handle}` : 'no handle'}</p>
                  </div>
                  <div className="admin__post-actions">
                    <ImpersonateButton targetId={m.id} targetName={m.display_name || m.handle || 'user'} />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

function ImpersonateButton({ targetId, targetName }: { targetId: string; targetName: string }) {
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const refresh = sess.session?.refresh_token;
      if (!token || !refresh) throw new Error('No session');

      // Stash developer session so "Exit" can restore it.
      stashDeveloperSession({ access_token: token, refresh_token: refresh });

      const res = await fetch('/api/admin/impersonate', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ target_user_id: targetId, reason: 'Manual impersonation from /admin' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? 'Impersonation denied.');
      }
      const { session_id, token_hash, target_display_name } = await res.json();

      // Redeem the one-time hashed token as a session swap. This signs the
      // browser in AS the target user without sending an email. When verifying
      // a hashed token, supabase-js requires ONLY token_hash + type — passing
      // `email` here triggers "Only the token_hash and type should be provided".
      const { error } = await supabase.auth.verifyOtp({
        token_hash,
        type: 'magiclink',
      });
      if (error) throw error;

      setImpersonationState({
        active: true,
        session_id,
        target_display_name: target_display_name || targetName,
      });
      window.location.assign('/');
    } catch (err: any) {
      alert(err?.message ?? 'Impersonation failed.');
      setBusy(false);
    }
  }

  return (
    <button type="button" className="btn btn--secondary btn--sm" onClick={go} disabled={busy}>
      {busy ? '…' : 'Sign in as'}
    </button>
  );
}

// ===========================================================================
// Admins panel — developer-only. Lists admins and toggles per-admin
// permissions. The first permission is can_impersonate, which lets an admin
// use the impersonation flow that's otherwise developer-only.
// ===========================================================================
function AdminsPanel({ active, meId }: { active: boolean; meId: string }) {
  const toast = useToast();
  const [admins, setAdmins] = useState<MemberRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const all = await fetchMembers();
      // Admins (and developers other than yourself) can hold permissions.
      setAdmins(all.filter((m) => !!m.user_id && (m.role === 'admin' || m.role === 'developer')));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load admins.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  async function togglePermission(m: MemberRow, key: string, value: boolean) {
    if (!m.user_id) return;
    setSavingId(m.user_id);
    try {
      await setAdminPermission({ userId: m.user_id, key, value });
      toast.success(
        value
          ? `${m.display_name || m.email} can now impersonate members.`
          : `Impersonation disabled for ${m.display_name || m.email}.`,
      );
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Update failed.');
    } finally {
      setSavingId(null);
    }
  }

  if (!loaded && loading) {
    return (
      <>
        <div className="admin__panel-head"><h2>Admins</h2></div>
        <p className="admin__note">Loading…</p>
      </>
    );
  }

  return (
    <>
      <div className="admin__panel-head">
        <h2>Admins</h2>
        <span className="admin__count">{admins.length}</span>
      </div>

      {error && (
        <div className="admin__error">
          <p className="kicker">Problem</p>
          <p>{error}</p>
        </div>
      )}

      <p className="admin__note">
        Grant per-admin permissions. <strong>Impersonation</strong> lets an admin sign in as any
        member to see their view, every session is written to <code>impersonation_log</code>.
        Developers always have this. Only developers can change these flags.
      </p>

      {admins.length === 0 ? (
        <EmptyState
          tone="compact"
          align="start"
          eyebrow="No admins"
          headline="No admins yet."
          body="Promote a member to Admin from the Members tab first."
        />
      ) : (
        <div className="admin__table-wrap">
          <table className="admin__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Impersonation</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((m) => {
                const isDev = m.role === 'developer';
                const canImp = isDev || m.permissions?.can_impersonate === true;
                const isSelf = m.user_id === meId;
                return (
                  <tr key={m.user_id}>
                    <td className="admin__cell-name">
                      <span className="admin__person">
                        <Avatar name={m.display_name || m.email} size="sm" founding={m.is_founding_member} />
                        <span>
                          {m.display_name || '—'}
                          {isSelf && <span className="pill pill--you" style={{ marginLeft: 6 }}>you</span>}
                          {m.handle && (
                            <span style={{ color: 'var(--text-soft)', display: 'block', fontSize: '0.85em' }}>
                              @{m.handle}
                            </span>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className="admin__cell-email"><code title={m.email}>{m.email}</code></td>
                    <td><span className="pill pill--role">{m.role === 'developer' ? 'Developer' : 'Admin'}</span></td>
                    <td>
                      {isDev ? (
                        <span style={{ color: 'var(--text-soft)', fontSize: '0.9em' }} title="Developers always can.">
                          Always
                        </span>
                      ) : (
                        <button
                          type="button"
                          className={`btn btn--sm ${canImp ? 'btn--secondary' : 'btn--ghost'}`}
                          style={{ fontSize: '0.85em' }}
                          onClick={() => togglePermission(m, 'can_impersonate', !canImp)}
                          disabled={savingId === m.user_id}
                          aria-pressed={canImp}
                        >
                          {savingId === m.user_id ? '…' : canImp ? '✓ Enabled' : 'Enable'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ===========================================================================
// Billing panel — admin-controlled pricing.
// Lazy-loads its data on first activation so the main Admin tab doesn't
// pay the cost when the user doesn't open Billing.
//
// Founding members are managed from the Members tab (the per-row founding
// toggle). is_founding_member grants a comped subscription that bypasses
// the paywall; on first sign-in, on_auth_user_created comps ONLY founders.
// ===========================================================================
function BillingPanel({ active }: { active: boolean }) {
  const toast = useToast();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pricing state
  const [activePrices, setActivePrices] = useState<{ monthly: PriceRow | null; annual: PriceRow | null }>({ monthly: null, annual: null });
  const [history, setHistory] = useState<PriceRow[]>([]);
  const [monthlyDraft, setMonthlyDraft] = useState('');
  const [annualDraft, setAnnualDraft] = useState('');
  const [savingInterval, setSavingInterval] = useState<'monthly' | 'annual' | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const prices = await fetchPrices();
      setActivePrices(prices.active);
      setHistory(prices.history);
      // Seed drafts with current values so admin sees what's set.
      setMonthlyDraft(prices.active.monthly ? (prices.active.monthly.unit_amount_cents / 100).toString() : '');
      setAnnualDraft(prices.active.annual ? (prices.active.annual.unit_amount_cents / 100).toString() : '');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load billing.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  async function handleSavePrice(interval: 'monthly' | 'annual', draft: string) {
    const dollars = Number.parseFloat(draft);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      toast.error('Enter a valid amount.');
      return;
    }
    const amount_cents = Math.round(dollars * 100);
    if (amount_cents < 100) {
      toast.error('Amount must be at least $1.');
      return;
    }

    const current = activePrices[interval];
    const currentLabel = current ? formatCents(current.unit_amount_cents, current.currency) : '(none set)';
    const newLabel = formatCents(amount_cents);

    const ok = window.confirm(
      `Change ${interval} price from ${currentLabel} to ${newLabel}?\n\n` +
      `• Existing subscribers stay on their current price (no impact)\n` +
      `• Anyone who signs up from now on will be charged ${newLabel}\n\n` +
      `This creates a new Price in Stripe and archives the old one in our records. ` +
      `It cannot be undone via this form: you'd need to set the price back manually.`,
    );
    if (!ok) return;

    setSavingInterval(interval);
    try {
      await createPrice({ interval, amount_cents });
      toast.success(`${interval[0].toUpperCase()}${interval.slice(1)} price updated to ${newLabel}.`);
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update price.');
    } finally {
      setSavingInterval(null);
    }
  }

  if (!loaded && loading) {
    return (
      <>
        <div className="admin__panel-head"><h2>Billing</h2></div>
        <p className="admin__note">Loading…</p>
      </>
    );
  }

  return (
    <>
      <div className="admin__panel-head">
        <h2>Billing</h2>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={refresh}
          disabled={loading}
        >
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="admin__error">
          <p className="kicker">Problem</p>
          <p>{error}</p>
        </div>
      )}

      {/* Pricing -------------------------------------------------------- */}
      <div className="admin__panel-head" style={{ marginTop: 'var(--space-8)' }}>
        <h3 style={{ margin: 0 }}>Subscription pricing</h3>
      </div>
      <p className="admin__note">
        Changing a price creates a new Stripe Price for new signups. Existing subscribers are
        grandfathered: they continue to be charged whatever they originally signed up for, until
        they cancel or you move them manually in the Stripe Dashboard.
      </p>

      <div className="admin__add">
        <div className="admin__add-row">
          <label>
            <span>Monthly price (USD)</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="1"
              placeholder="15.00"
              value={monthlyDraft}
              onChange={(e) => setMonthlyDraft(e.target.value)}
              disabled={savingInterval !== null}
            />
            <small style={{ display: 'block', marginTop: 4, color: 'var(--text-soft)' }}>
              Currently: {activePrices.monthly ? formatCents(activePrices.monthly.unit_amount_cents) : 'not set'}
            </small>
          </label>
          <div className="admin__add-actions">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => handleSavePrice('monthly', monthlyDraft)}
              disabled={savingInterval !== null || !monthlyDraft}
            >
              {savingInterval === 'monthly' ? '…' : 'Save monthly'}
            </button>
          </div>
        </div>

        <div className="admin__add-row">
          <label>
            <span>Annual price (USD)</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="1"
              placeholder="150.00"
              value={annualDraft}
              onChange={(e) => setAnnualDraft(e.target.value)}
              disabled={savingInterval !== null}
            />
            <small style={{ display: 'block', marginTop: 4, color: 'var(--text-soft)' }}>
              Currently: {activePrices.annual ? formatCents(activePrices.annual.unit_amount_cents) : 'not set'}
            </small>
          </label>
          <div className="admin__add-actions">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => handleSavePrice('annual', annualDraft)}
              disabled={savingInterval !== null || !annualDraft}
            >
              {savingInterval === 'annual' ? '…' : 'Save annual'}
            </button>
          </div>
        </div>
      </div>

      {/* History ------------------------------------------------------- */}
      {history.length > 0 && (
        <>
          <div className="admin__panel-head" style={{ marginTop: 'var(--space-8)' }}>
            <h3 style={{ margin: 0 }}>Price history</h3>
            <span className="admin__count">{history.length}</span>
          </div>
          <table className="admin__table">
            <thead>
              <tr>
                <th>Interval</th>
                <th>Tier</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Created</th>
                <th>Stripe Price ID</th>
              </tr>
            </thead>
            <tbody>
              {history.map((p) => (
                <tr key={p.id}>
                  <td>{p.interval}</td>
                  <td>{p.tier}</td>
                  <td>{formatCents(p.unit_amount_cents, p.currency)}</td>
                  <td>
                    <span className={`pill pill--${p.is_active ? 'active' : 'invited'}`}>
                      {p.is_active ? 'active' : 'archived'}
                    </span>
                  </td>
                  <td>{new Date(p.created_at).toLocaleDateString()}</td>
                  <td><code style={{ fontSize: '0.75em' }}>{p.stripe_price_id}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Founding members live in the Members tab. */}
      <div className="admin__panel-head" style={{ marginTop: 'var(--space-8)' }}>
        <h3 style={{ margin: 0 }}>Founding members</h3>
      </div>
      <p className="admin__note">
        Founding members are managed from the <strong>Members</strong> tab: toggle a member's
        <strong> Founding member</strong> status and they'll bypass the paywall (comped subscription).
      </p>
    </>
  );
}

// ===========================================================================
// Appearance panel — runtime brand accent (admin/seasonal lock).
// This intentionally diverges from the brand handoff's "build-time, no JS,
// one commit" delivery (the team asked to set it from /admin without a
// deploy) but keeps brand *intent*: admin-only, exactly one accent at a
// time, pure black-&-white by default. Presets = the vetted v5 palette;
// a custom colour is allowed but flagged. See src/lib/accent.ts.
// ===========================================================================
function AppearancePanel({ active }: { active: boolean }) {
  const toast = useToast();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverValue, setServerValue] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [customInput, setCustomInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const v = await fetchAccent();
      setServerValue(v.accent);
      setSelected(v.accent);
      setCustomInput(v.accent ?? '');
      setInputError(null);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  function pickPreset(hex: string) {
    setSelected(hex);
    setCustomInput(hex);
    setInputError(null);
  }

  function onCustomChange(raw: string) {
    setCustomInput(raw);
    if (raw.trim() === '') {
      setSelected(null);
      setInputError(null);
      return;
    }
    try {
      setSelected(parseColour(raw));
      setInputError(null);
    } catch (e: any) {
      setInputError(e?.message ?? 'Invalid colour.');
    }
  }

  function resetBW() {
    setSelected(null);
    setCustomInput('');
    setInputError(null);
  }

  const dirty = (selected ?? null) !== (serverValue ?? null);
  const isVetted = selected
    ? ACCENT_PRESETS.some((p) => p.hex.toLowerCase() === selected.toLowerCase())
    : true; // B&W is the brand default

  async function save() {
    if (inputError) {
      toast.error('Fix the colour first.');
      return;
    }
    setSaving(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Sign in first.');
      const stored = await saveAccent({ accent: selected, accent_strong: selected }, token);
      applyAccent(stored); // repaint the whole app for this admin immediately
      setServerValue(stored.accent);
      setSelected(stored.accent);
      setCustomInput(stored.accent ?? '');
      toast.success(
        stored.accent
          ? 'Accent saved. Other members pick it up on next load (within ~1 min).'
          : 'Reset to black & white. Other members update on next load.',
      );
    } catch (err: any) {
      toast.error(err?.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  if (!loaded && loading) {
    return (
      <>
        <div className="admin__panel-head"><h2>Appearance</h2></div>
        <p className="admin__note">Loading…</p>
      </>
    );
  }

  // Scoped preview: redeclare --accent/--accent-strong on the wrapper so
  // the sample reflects `selected` without touching the rest of the app.
  const previewVars = {
    ['--accent']: selected ?? '#14110F',
    ['--accent-strong']: selected ?? '#FAFAF7',
  } as React.CSSProperties;

  return (
    <>
      <div className="admin__panel-head">
        <h2>Appearance</h2>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={refresh}
          disabled={loading || saving}
        >
          {loading ? '…' : 'Refresh'}
        </button>
      </div>
      <p className="admin__note">
        The brand is black-&-white by default. Set <strong>one</strong> accent and it
        cascades across the app: rules, buttons, pills, links, tinted halftones.
        Headlines and body stay carbon. This is an admin/seasonal decision; members
        never pick.
      </p>

      <div className="admin__panel-head" style={{ marginTop: 'var(--space-6)' }}>
        <h3 style={{ margin: 0 }}>Brand palette</h3>
      </div>
      <div className="appearance__swatches">
        {ACCENT_PRESETS.map((p) => {
          const on = selected?.toLowerCase() === p.hex.toLowerCase();
          return (
            <button
              key={p.name}
              type="button"
              className={`appearance__swatch ${on ? 'appearance__swatch--on' : ''}`}
              style={{ background: p.hex }}
              onClick={() => pickPreset(p.hex)}
              title={`${p.family} · ${p.tone} (${p.hex})`}
              aria-label={`${p.family} ${p.tone}`}
              aria-pressed={on}
            >
              {on ? <span className="appearance__swatch-check" aria-hidden>✓</span> : null}
            </button>
          );
        })}
      </div>

      <div className="admin__panel-head" style={{ marginTop: 'var(--space-6)' }}>
        <h3 style={{ margin: 0 }}>Custom colour</h3>
      </div>
      <div className="admin__add">
        <div className="admin__add-row">
          <label style={{ flex: 1 }}>
            <span>Hex or RGB</span>
            <input
              type="text"
              placeholder="#C97D58 or rgb(201, 125, 88)"
              value={customInput}
              onChange={(e) => onCustomChange(e.target.value)}
              disabled={saving}
              spellCheck={false}
            />
            {inputError && (
              <small style={{ display: 'block', marginTop: 4, color: 'var(--text-soft)' }}>
                {inputError}
              </small>
            )}
          </label>
          <label>
            <span>Pick</span>
            <input
              type="color"
              value={selected ?? '#141110'}
              onChange={(e) => {
                setSelected(e.target.value);
                setCustomInput(e.target.value);
                setInputError(null);
              }}
              disabled={saving}
              style={{ width: 56, height: 40, padding: 2 }}
              aria-label="Colour picker"
            />
          </label>
        </div>
        {!isVetted && (
          <small style={{ display: 'block', marginTop: 6, color: 'var(--text-soft)' }}>
            Heads up, this is outside the brand-vetted v5 palette. The brand
            team's #1 rule is restraint; prefer a preset unless there's a
            deliberate reason.
          </small>
        )}
      </div>

      <div className="admin__panel-head" style={{ marginTop: 'var(--space-6)' }}>
        <h3 style={{ margin: 0 }}>Preview</h3>
      </div>
      <div className="appearance__preview" style={previewVars}>
        <span className="appearance__preview-rule" aria-hidden />
        <button type="button" className="btn btn--accent btn--sm" tabIndex={-1}>
          Accent button
        </button>
        <span className="pill pill--badge" style={{ ['--chip']: 'var(--accent)' } as React.CSSProperties}>
          Pill
        </span>
        <a
          href="#appearance"
          onClick={(e) => e.preventDefault()}
          style={{ color: 'var(--accent)', fontWeight: 600 }}
        >
          A link
        </a>
        <span className="appearance__preview-label">
          {selected ? selected : 'Black & white (default)'}
        </span>
      </div>

      <div className="admin__row-actions" style={{ marginTop: 'var(--space-6)', gap: 'var(--space-4)' }}>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={save}
          disabled={saving || !dirty || !!inputError}
        >
          {saving ? '…' : dirty ? 'Save accent' : 'Saved'}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={resetBW}
          disabled={saving || selected === null}
        >
          Reset to black &amp; white
        </button>
        {dirty && (
          <span style={{ color: 'var(--text-soft)', fontSize: '0.9em', alignSelf: 'center' }}>
            Unsaved
          </span>
        )}
      </div>
    </>
  );
}

// ===========================================================================
// Members panel — search, filter, toggle founding status, manage roles, etc.
// Lazy-loads on first activation. Owns the admin/members data; the original
// Testers tab logic is fully replaced here.
// ===========================================================================
function MembersPanel({ active, me }: { active: boolean; me: { id: string; role: Role } }) {
  const toast = useToast();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [foundingOnly, setFoundingOnly] = useState(false);

  // Bulk-email selection — a Set of lowercased emails so it survives filtering
  // and pagination (selecting on page 1 then sending stays intact on page 2).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [emailOpen, setEmailOpen] = useState(false);

  function toggleSelect(email: string) {
    const key = email.toLowerCase();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  // Pagination — client-side; server returns everything sorted by recent activity.
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, foundingOnly]);

  // Add form
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [addFounding, setAddFounding] = useState(false);
  const [addBusy, setAddBusy] = useState(false);

  // Per-row state
  const [savingId, setSavingId] = useState<string | null>(null);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);

  // Role / profile / sign-in-link per-row state.
  const [roleSavingId, setRoleSavingId] = useState<string | null>(null);
  const [profileEditId, setProfileEditId] = useState<string | null>(null);
  const [pName, setPName] = useState('');
  const [pHandle, setPHandle] = useState('');
  const [profileSavingId, setProfileSavingId] = useState<string | null>(null);
  const [linkBusyId, setLinkBusyId] = useState<string | null>(null);
  // Which row's actions menu is open (one at a time). Keyed by user_id.
  const [actionsMenuId, setActionsMenuId] = useState<string | null>(null);
  // Per-email busy flag for the "Send invite" / "Resend invite" button on
  // rows that haven't signed in yet. Keyed by email since these rows have
  // no user_id.
  const [invitingEmail, setInvitingEmail] = useState<string | null>(null);

  // Hard-delete confirmation modal. Holds the row being deleted; the admin must
  // type the email to confirm. reinvite re-adds a fresh whitelist row.
  const [deleteTarget, setDeleteTarget] = useState<MemberRow | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteReinvite, setDeleteReinvite] = useState(false);
  const [deleteReinviteFounding, setDeleteReinviteFounding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const ROLE_LABEL: Record<Role, string> = {
    user: 'Member',
    admin: 'Admin',
    developer: 'Developer',
  };

  // Which roles can `me` assign to this row? null = no role control
  // (not signed in, the caller themselves, or out of the caller's reach).
  function roleOptionsFor(target: MemberRow): Role[] | null {
    if (!target.user_id) return null;
    if (target.user_id === me.id) return null;
    if (me.role === 'developer') return ['user', 'admin', 'developer'];
    if (me.role === 'admin') {
      if (target.role === 'developer') return null; // admins don't manage devs
      return ['user', 'admin'];
    }
    return null;
  }

  async function changeRole(member: MemberRow, next: Role) {
    if (!member.user_id || next === member.role) return;
    const who = member.display_name || member.email;
    const ok = window.confirm(
      `Change ${who}'s role from ${ROLE_LABEL[(member.role ?? 'user') as Role]} ` +
      `to ${ROLE_LABEL[next]}?\n\n` +
      (next === 'admin' || next === 'developer'
        ? `They'll gain ${next === 'developer' ? 'developer' : 'admin'} access to /admin ` +
          `(and free Club access).\n\n`
        : `They'll lose staff access and fall back to a normal member.\n\n`) +
      `Takes effect on their next login, or within ~1 hour as their session refreshes.`,
    );
    if (!ok) return;
    setRoleSavingId(member.user_id);
    try {
      const res = await setMemberRole({ userId: member.user_id, role: next });
      toast.success(`${who} is now ${ROLE_LABEL[res.role]}. ${res.note}`);
      await refresh();
    } catch (err: any) {
      toast.error(friendlyRoleError(err?.message));
    } finally {
      setRoleSavingId(null);
    }
  }

  function friendlyRoleError(msg?: string): string {
    switch (msg) {
      case 'cannot_change_own_role':
        return "You can't change your own role.";
      case 'developer_role_requires_developer':
        return 'Only a developer can grant or change the developer role.';
      case 'cannot_remove_last_admin':
        return "Can't demote the last admin/developer, promote someone else first.";
      case 'forbidden':
        return 'You do not have permission for that.';
      default:
        return msg || 'Role change failed.';
    }
  }

  function startProfileEdit(member: MemberRow) {
    if (!member.user_id) return;
    setProfileEditId(member.user_id);
    setPName(member.display_name ?? '');
    setPHandle(member.handle ?? '');
  }

  function cancelProfileEdit() {
    setProfileEditId(null);
    setPName('');
    setPHandle('');
  }

  async function saveProfile(member: MemberRow) {
    if (!member.user_id) return;
    setProfileSavingId(member.user_id);
    try {
      await updateMemberProfile({
        userId: member.user_id,
        display_name: pName.trim() || null,
        handle: pHandle.trim() ? pHandle.trim() : null,
      });
      toast.success('Profile updated.');
      cancelProfileEdit();
      await refresh();
    } catch (err: any) {
      const m = err?.message ?? '';
      toast.error(
        /handle_taken/.test(m)
          ? 'That @handle is already taken.'
          : /invalid_handle/.test(m)
            ? 'Handle must be 2–30 chars: a–z, 0–9, underscore.'
            : m || 'Update failed.',
      );
    } finally {
      setProfileSavingId(null);
    }
  }

  async function sendInvite(member: MemberRow) {
    const isResend = (member.invite_count ?? 0) > 0;
    const who = member.display_name || member.email;
    const ok = window.confirm(
      (isResend
        ? `Resend the invite email to ${who} (${member.email})?\n\n`
        : `Send an invite email to ${who} (${member.email})?\n\n`) +
      `They'll get a branded email with a single-use sign-in link. The button on ` +
      `this row will switch to "Resend invite" until they actually log in, then ` +
      `disappear.`,
    );
    if (!ok) return;
    setInvitingEmail(member.email);
    try {
      const res = await sendMemberInvite(member.email);
      toast.success(
        res.warn
          ? `${isResend ? 'Resent' : 'Sent'}: ${res.warn}`
          : `${isResend ? 'Resent' : 'Invite sent'} to ${member.email}.`,
      );
      // Optimistic local update so the button label flips to "Resend"
      // immediately; refresh below picks up the canonical timestamp.
      setMembers((prev) =>
        prev.map((m) =>
          m.email.toLowerCase() === member.email.toLowerCase()
            ? { ...m, last_invited_at: new Date().toISOString(), invite_count: res.invite_count }
            : m,
        ),
      );
      // Quietly re-pull in the background to catch any server-side changes
      // (e.g. status flip) without a visible loading state.
      void refresh();
    } catch (err: any) {
      const msg = err?.message ?? 'Invite failed.';
      toast.error(
        /email_not_configured/.test(msg)
          ? 'Email not set up yet: RESEND_API_KEY / INVITE_FROM_EMAIL must be configured on the Cloudflare Pages project.'
          : msg,
      );
    } finally {
      setInvitingEmail(null);
    }
  }

  async function copySignInLink(member: MemberRow) {
    if (!member.user_id) return;
    setLinkBusyId(member.user_id);
    try {
      const { link } = await generateSignInLink(member.user_id);
      try {
        await navigator.clipboard.writeText(link);
        toast.success('Single-use sign-in link copied, share only with them.');
      } catch {
        window.prompt('Copy this single-use sign-in link:', link);
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not generate a link.');
    } finally {
      setLinkBusyId(null);
    }
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const mems = await fetchMembers();
      setMembers(mems);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load members.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Filter
  const filtered = members.filter((m) => {
    if (search) {
      const q = search.trim().toLowerCase();
      const hay = `${m.email} ${m.display_name ?? ''} ${m.handle ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (foundingOnly && !m.is_founding_member) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const visible = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  // Select-all operates on the whole filtered set (not just the visible page).
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((m) => selected.has(m.email.toLowerCase()));
  function toggleSelectAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (filtered.every((m) => next.has(m.email.toLowerCase()))) {
        filtered.forEach((m) => next.delete(m.email.toLowerCase()));
      } else {
        filtered.forEach((m) => next.add(m.email.toLowerCase()));
      }
      return next;
    });
  }
  const selectedRecipients = members
    .filter((m) => selected.has(m.email.toLowerCase()))
    .map((m) => ({ email: m.email, display_name: m.display_name }));

  // Format ISO date → relative time + tooltip with full date.
  function fmtTimestamp(iso: string | null): { label: string; title: string } {
    if (!iso) return { label: '—', title: '' };
    const d = new Date(iso);
    const now = Date.now();
    const diffSec = Math.floor((now - d.getTime()) / 1000);
    let label = '';
    if (diffSec < 60) label = 'just now';
    else if (diffSec < 3600) label = `${Math.floor(diffSec / 60)}m ago`;
    else if (diffSec < 86400) label = `${Math.floor(diffSec / 3600)}h ago`;
    else if (diffSec < 86400 * 30) label = `${Math.floor(diffSec / 86400)}d ago`;
    else label = d.toLocaleDateString();
    return { label, title: d.toLocaleString() };
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    // A member is keyed by their email — never create a row without a valid one
    // (a blank/garbled email is what created the orphaned, login-less record).
    const email = addEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email address.');
      return;
    }
    setAddBusy(true);
    try {
      await addAllowedEmail({
        email,
        display_name: addName.trim() || null,
        is_founding_member: addFounding,
      });
      toast.success('Member added.');
      setAddEmail('');
      setAddName('');
      setAddFounding(false);
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to add.');
    } finally {
      setAddBusy(false);
    }
  }

  async function handleRemoveFromWhitelist(email: string) {
    const ok = window.confirm(
      `Cancel the invite for ${email}?\n\n` +
      `Removes them from the invite list. It does NOT delete an existing account. Continue?`,
    );
    if (!ok) return;
    setRemovingEmail(email);
    try {
      await removeAllowedEmail(email);
      toast.success('Removed from whitelist.');
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Remove failed.');
    } finally {
      setRemovingEmail(null);
    }
  }

  // Toggle a member's founding status. true grants comped Club access
  // (bypasses the paywall); false revokes it (paid subs untouched). Both
  // directions confirm inline since they change billing/access.
  async function toggleFounding(member: MemberRow, next: boolean) {
    // Not-yet-signed-in rows have no profile, so we set the seed value on the
    // whitelist instead; it's copied onto their profile at first login.
    if (!member.user_id) {
      setSavingId(member.email);
      try {
        await setAllowedEmailFounding(member.email, next);
        toast.success(
          next
            ? "Marked as founding: they'll get free Club access when they first sign in."
            : "Founding removed: they'll go through the paywall when they sign in.",
        );
        await refresh();
      } catch (err: any) {
        toast.error(err?.message ?? 'Update failed.');
      } finally {
        setSavingId(null);
      }
      return;
    }
    const who = member.display_name || member.email;
    const ok = window.confirm(
      next
        ? `Make ${who} a founding member?\n\n` +
          `Gives free Club access + the Founding badge. Takes effect within ~1 hr (instantly on re-login).`
        : `Remove founding status from ${who}?\n\n` +
          `Ends their free Club access (any paid subscription stays). Account, posts and comments are kept.`,
    );
    if (!ok) return;
    setSavingId(member.user_id);
    try {
      await setMemberFounding({ userId: member.user_id, isFounding: next });
      toast.success(
        next
          ? `${who} is now a founding member.`
          : `Founding status removed. They'll hit the paywall on next session refresh.`,
      );
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Update failed.');
    } finally {
      setSavingId(null);
    }
  }

  // Open the hard-delete confirmation modal for a signed-in member.
  function openDelete(member: MemberRow) {
    if (!member.user_id) return;
    setDeleteTarget(member);
    setDeleteConfirm('');
    setDeleteReinvite(false);
    setDeleteReinviteFounding(false);
  }

  function closeDelete() {
    setDeleteTarget(null);
    setDeleteConfirm('');
    setDeleteReinvite(false);
    setDeleteReinviteFounding(false);
  }

  // Permanently delete the member. Their auth login + profile go; their posts/
  // uploads/comments are reassigned to "Deleted member". Guarded server-side.
  async function confirmDelete() {
    const member = deleteTarget;
    if (!member?.user_id) return;
    setDeletingId(member.user_id);
    try {
      const res = await deleteMember({
        userId: member.user_id,
        reinvite: deleteReinvite,
        reinviteFounding: deleteReinvite && deleteReinviteFounding,
      });
      toast.success(
        res.reinvited
          ? `${member.email} deleted and re-invited for a fresh start.`
          : `${member.display_name || member.email} permanently deleted.`,
      );
      closeDelete();
      await refresh();
    } catch (err: any) {
      toast.error(friendlyDeleteError(err?.message));
    } finally {
      setDeletingId(null);
    }
  }

  function friendlyDeleteError(msg?: string): string {
    switch (msg) {
      case 'cannot_delete_self':
        return "You can't delete your own account.";
      case 'cannot_delete_last_admin':
        return "Can't delete the last admin/developer, promote someone else first.";
      case 'developer_required_for_staff':
        return 'Only a developer can delete another admin/developer.';
      case 'ghost_member_missing':
        return 'The "Deleted member" placeholder is missing: apply migration 052 first.';
      case 'profile_not_found':
        return 'That member no longer exists.';
      default:
        return msg || 'Delete failed.';
    }
  }

  if (!loaded && loading) {
    return (
      <>
        <div className="admin__panel-head"><h2>Members</h2></div>
        <p className="admin__note">Loading…</p>
      </>
    );
  }

  return (
    <>
      <div className="admin__panel-head">
        <h2>Members</h2>
        <span className="admin__count">{filtered.length} of {members.length}</span>
      </div>

      {error && (
        <div className="admin__error">
          <p className="kicker">Problem</p>
          <p>{error}</p>
        </div>
      )}

      {/* Add form ----------------------------------------------------- */}
      <form className="admin__add" onSubmit={handleAdd}>
        <div className="admin__add-row">
          <label>
            <span>Email</span>
            <input
              type="email"
              required
              placeholder="name@example.com"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              disabled={addBusy}
            />
          </label>
          <label>
            <span>Display name</span>
            <input
              type="text"
              placeholder="Optional"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              disabled={addBusy}
            />
          </label>
          <div className="admin__add-actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={addBusy || !addEmail}>
              {addBusy ? '…' : 'Add member'}
            </button>
          </div>
        </div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={addFounding}
              onChange={(e) => setAddFounding(e.target.checked)}
              disabled={addBusy}
            />
            <span className="onb__label" style={{ margin: 0 }}>Founding member</span>
          </label>
          <small style={{ display: 'block', marginTop: 6, color: 'var(--text-soft)' }}>
            Founding members get free Club access (a comped subscription) on first sign-in and the
            Founding Member badge. Leave off and they'll go through the paywall.
          </small>
        </div>
      </form>

      {/* Filters ------------------------------------------------------ */}
      <div className="admin__add" style={{ marginTop: 'var(--space-4)' }}>
        <div className="admin__add-row">
          <label style={{ flex: 1 }}>
            <span>Search</span>
            <input
              type="search"
              placeholder="Email, name, or @handle"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="admin__add-actions">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={refresh}
              disabled={loading}
            >
              {loading ? '…' : 'Refresh'}
            </button>
          </div>
        </div>
        <div style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: 'var(--text-soft)', fontSize: '0.9em' }}>Filter:</span>
          <button
            type="button"
            className={`btn btn--sm ${foundingOnly ? 'btn--secondary' : 'btn--ghost'}`}
            style={{ fontSize: '0.85em' }}
            onClick={() => setFoundingOnly((v) => !v)}
            aria-pressed={foundingOnly}
          >
            {foundingOnly ? '✓ ' : ''}Founding members only
          </button>
        </div>
      </div>

      {/* Bulk-email selection toolbar -------------------------------- */}
      {selected.size > 0 && (
        <div style={{
          display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap',
          margin: 'var(--space-3) 0', padding: '10px 14px',
          background: 'var(--bone, #f6f4ef)', border: '1px solid var(--smoke)', borderRadius: 'var(--radius-sm)',
        }}>
          <strong>{selected.size} selected</strong>
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setEmailOpen(true)}>
            Email selected
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSelected(new Set())}>
            Clear selection
          </button>
        </div>
      )}

      {/* Table -------------------------------------------------------- */}
      {filtered.length === 0 ? (
        <EmptyState
          tone="compact"
          align="start"
          eyebrow="No matches"
          headline={members.length === 0 ? 'No members yet.' : 'No one matches that filter.'}
          body={members.length === 0 ? 'Add one above to get started.' : 'Adjust the search or filter to see more.'}
        />
      ) : (
        <div className="admin__table-wrap">
        <table className="admin__table">
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                  aria-label="Select all matching members"
                  title="Select all matching members"
                />
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Role</th>
              <th>Founding</th>
              <th>First login</th>
              <th>Last login</th>
              <th aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((m) => {
              const pEditing = !!m.user_id && m.user_id === profileEditId;
              const isSelf = !!m.user_id && m.user_id === me.id;
              const roleOpts = roleOptionsFor(m);
              const first = fmtTimestamp(m.first_login_at);
              const last = fmtTimestamp(m.last_sign_in_at);
              return (
                <tr key={m.email + (m.user_id ?? '')}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(m.email.toLowerCase())}
                      onChange={() => toggleSelect(m.email)}
                      aria-label={`Select ${m.display_name || m.email}`}
                    />
                  </td>
                  <td className="admin__cell-name">
                    {pEditing ? (
                      <div className="admin__profile-edit">
                        <input
                          type="text"
                          value={pName}
                          placeholder="Display name"
                          onChange={(e) => setPName(e.target.value)}
                          disabled={profileSavingId === m.user_id}
                          aria-label="Display name"
                        />
                        <input
                          type="text"
                          value={pHandle}
                          placeholder="handle"
                          onChange={(e) => setPHandle(e.target.value)}
                          disabled={profileSavingId === m.user_id}
                          aria-label="Handle"
                          spellCheck={false}
                        />
                      </div>
                    ) : (
                      <span className="admin__person">
                        <Avatar
                          name={m.display_name || m.email}
                          size="sm"
                          founding={m.is_founding_member}
                        />
                        <span>
                          {m.display_name || '—'}
                          {isSelf && (
                            <span className="pill pill--you" style={{ marginLeft: 6 }}>you</span>
                          )}
                          {m.handle && (
                            <span style={{ color: 'var(--text-soft)', display: 'block', fontSize: '0.85em' }}>
                              @{m.handle}
                            </span>
                          )}
                        </span>
                      </span>
                    )}
                  </td>
                  <td className="admin__cell-email"><code title={m.email}>{m.email}</code></td>
                  <td>
                    {m.status ? (
                      <span className={`pill pill--${m.status}`}>{m.status}</span>
                    ) : (
                      <span style={{ color: 'var(--text-soft)' }}>—</span>
                    )}
                  </td>
                  <td>
                    {roleOpts ? (
                      <select
                        className="admin__role-select"
                        value={(m.role ?? 'user') as Role}
                        disabled={roleSavingId === m.user_id}
                        onChange={(e) => changeRole(m, e.target.value as Role)}
                        aria-label={`Role for ${m.display_name || m.email}`}
                      >
                        {roleOpts.map((r) => (
                          <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                        ))}
                        {/* Keep the current role visible even if it's outside
                            the options the caller can assign (e.g. developer
                            shown to an admin as read-only). */}
                        {!roleOpts.includes((m.role ?? 'user') as Role) && (
                          <option value={(m.role ?? 'user') as Role}>
                            {ROLE_LABEL[(m.role ?? 'user') as Role]}
                          </option>
                        )}
                      </select>
                    ) : (
                      <span
                        className="pill pill--role"
                        title={isSelf ? "You can't change your own role." : undefined}
                      >
                        {ROLE_LABEL[(m.role ?? 'user') as Role]}
                      </span>
                    )}
                  </td>
                  <td>
                    {m.is_founding_member ? (
                      <span
                        className="pill pill--badge"
                        style={{ ['--chip']: 'var(--accent)' } as React.CSSProperties}
                        title="Founding member, comped Club access."
                      >
                        Founding
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-soft)' }}>—</span>
                    )}
                  </td>
                  <td className="admin__cell-time" title={first.title}>{first.label}</td>
                  <td className="admin__cell-time" title={last.title}>{last.label}</td>
                  <td className="admin__row-actions">
                    {pEditing ? (
                      <>
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          onClick={() => saveProfile(m)}
                          disabled={profileSavingId === m.user_id}
                        >
                          {profileSavingId === m.user_id ? '…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={cancelProfileEdit}
                          disabled={profileSavingId === m.user_id}
                        >
                          Cancel
                        </button>
                      </>
                    ) : !m.user_id ? (
                      (() => {
                        const invited = fmtTimestamp(m.last_invited_at);
                        const wasInvited = !!m.last_invited_at;
                        return (
                          <>
                            <button
                              type="button"
                              className="btn btn--primary btn--sm"
                              onClick={() => sendInvite(m)}
                              disabled={invitingEmail === m.email}
                              title={
                                wasInvited
                                  ? `Last sent ${invited.title}. Hidden once they sign in.`
                                  : 'Email a branded single-use sign-in link.'
                              }
                            >
                              {invitingEmail === m.email
                                ? '…'
                                : wasInvited
                                  ? `Resend invite · ${invited.label}`
                                  : 'Send invite'}
                            </button>
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => toggleFounding(m, !m.is_founding_member)}
                              disabled={savingId === m.email}
                              title={
                                m.is_founding_member
                                  ? "Remove founding seed: they'll go through the paywall at first sign-in."
                                  : "Mark founding: they'll get free Club access at first sign-in."
                              }
                            >
                              {savingId === m.email
                                ? '…'
                                : m.is_founding_member
                                  ? 'Unmark founding'
                                  : 'Mark founding'}
                            </button>
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm admin__remove"
                              onClick={() => handleRemoveFromWhitelist(m.email)}
                              disabled={removingEmail === m.email}
                              title="Remove them from the invite list. Doesn't affect an existing account."
                            >
                              {removingEmail === m.email ? '…' : 'Cancel invite'}
                            </button>
                          </>
                        );
                      })()
                    ) : actionsMenuId === m.user_id ? (
                      <div className="admin__menu">
                        <div className="admin__menu-list" role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            className="btn btn--ghost btn--sm"
                            onClick={() => { setActionsMenuId(null); toggleFounding(m, !m.is_founding_member); }}
                            disabled={savingId === m.user_id}
                            title={
                              m.is_founding_member
                                ? 'Cancel comped access. They hit the paywall.'
                                : 'Grant free comped Club access + the Founding Member badge.'
                            }
                          >
                            {m.is_founding_member ? 'Remove founding' : 'Make founding member'}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="btn btn--ghost btn--sm"
                            onClick={() => { setActionsMenuId(null); startProfileEdit(m); }}
                          >
                            Edit profile
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="btn btn--ghost btn--sm"
                            onClick={() => { setActionsMenuId(null); copySignInLink(m); }}
                            disabled={linkBusyId === m.user_id}
                            title="Generate a single-use magic sign-in link to hand them."
                          >
                            {linkBusyId === m.user_id ? '…' : 'Copy sign-in link'}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="btn btn--ghost btn--sm admin__remove"
                            onClick={() => { setActionsMenuId(null); openDelete(m); }}
                            title="Permanently delete their account. Posts/comments are kept under 'Deleted member'."
                          >
                            Delete user…
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setActionsMenuId(null)}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="admin__menu">
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm admin__menu-toggle"
                          aria-haspopup="menu"
                          aria-expanded={false}
                          onClick={() => setActionsMenuId(m.user_id)}
                        >
                          Actions
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {filtered.length > PAGE_SIZE && (
        <div
          className="admin__toggle-row"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--space-3)' }}
        >
          <span style={{ color: 'var(--text-soft)', fontSize: '0.9em' }}>
            Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              ← Prev
            </button>
            <span style={{ color: 'var(--text-soft)', fontSize: '0.9em' }}>
              Page {safePage} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      <p className="admin__note" style={{ marginTop: 'var(--space-4)' }}>
        <strong>Send invite</strong> emails a member who hasn't signed in yet a branded
        single-use link, and the button reappears as <strong>Resend invite</strong> until they
        log in, then disappears.
        <strong> Role</strong> promotes/demotes staff: Admin and Developer reach <code>/admin</code>.
        It takes effect on their next login (or within ~1 hr as their session refreshes); you can't
        change your own role, and only a Developer can manage the Developer role.
        <strong> Edit profile</strong> sets their display name / @handle.
        <strong> Copy sign-in link</strong> makes a single-use magic link to onboard the team.
        <strong> Make / Remove founding member</strong> grants or revokes free comped Club access
        (bypasses the paywall) and the Founding Member badge, taking effect on their next session refresh.
        For someone who hasn't signed in yet, <strong>Mark founding</strong> sets the same thing on
        their invite so it applies the moment they first log in.
        <strong> Cancel invite</strong> (shown only before someone signs in) just clears their
        pending invite row — it never deletes an account.
        <strong> Delete user…</strong> is the only action that removes a signed-in member: it
        permanently deletes their login and profile, keeping their posts and comments under an
        anonymous “Deleted member”. Optionally re-invite them in the same step for a clean restart.
      </p>

      {deleteTarget && (
        <div
          className="admin__modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Delete user"
          onClick={(e) => { if (e.target === e.currentTarget && deletingId === null) closeDelete(); }}
        >
          <div className="admin__modal">
            <h3 style={{ marginTop: 0 }}>Delete this member?</h3>
            <p style={{ color: 'var(--text-soft)' }}>
              This permanently deletes{' '}
              <strong>{deleteTarget.display_name || deleteTarget.email}</strong>{' '}
              ({deleteTarget.email}): their login, profile, subscription and personal data.
              Their posts, uploads and comments are kept but reassigned to an anonymous
              “Deleted member”. <strong>This cannot be undone.</strong>
            </p>

            <label style={{ display: 'block', marginTop: 'var(--space-3)' }}>
              <span className="onb__label">Type their email to confirm</span>
              <input
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={deleteTarget.email}
                autoComplete="off"
                style={{ width: '100%' }}
              />
            </label>

            <label className="onb__check" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', marginTop: 'var(--space-3)' }}>
              <input
                type="checkbox"
                checked={deleteReinvite}
                onChange={(e) => setDeleteReinvite(e.target.checked)}
              />
              <span className="onb__label" style={{ margin: 0 }}>
                Re-invite this email afterwards (fresh start)
              </span>
            </label>
            {deleteReinvite && (
              <label className="onb__check" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', marginTop: 'var(--space-2)', marginLeft: 'var(--space-5)' }}>
                <input
                  type="checkbox"
                  checked={deleteReinviteFounding}
                  onChange={(e) => setDeleteReinviteFounding(e.target.checked)}
                />
                <span className="onb__label" style={{ margin: 0 }}>…as a founding member</span>
              </label>
            )}

            <div className="admin__row-actions" style={{ marginTop: 'var(--space-5)', gap: 'var(--space-3)' }}>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={closeDelete}
                disabled={deletingId !== null}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--sm admin__remove"
                onClick={confirmDelete}
                disabled={
                  deletingId !== null ||
                  deleteConfirm.trim().toLowerCase() !== deleteTarget.email.toLowerCase()
                }
              >
                {deletingId !== null ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {emailOpen && (
        <BulkEmailModal
          recipients={selectedRecipients}
          onClose={() => setEmailOpen(false)}
          onSent={() => { setSelected(new Set()); void refresh(); }}
        />
      )}
    </>
  );
}
